import { useState, useMemo, useEffect } from 'react';
import { AppModal } from '@/components/modals';

export function ApprovalActionDialog({ action, approvers = [], currentUser, userOptions = [], saving, onClose, onSubmit }) {
  const isTransfer = action?.kind === 'ticket-transfer';
  const isRework = action?.kind === 'ticket-rework' || action?.kind === 'reject';
  const isAttendanceApprove = action?.kind === 'approve' && action?.type === 'Attendance';
  const isTicketAction = action?.kind === 'ticket-approve' || action?.kind === 'ticket-rework';
  const isPC = String(currentUser?.role || currentUser?.Role || '').toLowerCase() === 'pc';

  const adminApprovers = useMemo(() => {
    return approvers.filter(a => 
      /^(super admin|admin)$/i.test(a.role || '') || 
      /nandlal/i.test(a.name || '') || 
      a.id === 'KRIS_001'
    );
  }, [approvers]);

  // Restrict to ONLY the assigned approver(s) for this ticket/employee
  const ticketAssignedApprovers = useMemo(() => {
    if (!isTicketAction) return adminApprovers;
    const row = action?.row || {};
    const approverId = String(row['Task Approver ID'] || row.ApproverID || '').trim();
    const approverName = String(row['Task Approver Name'] || row.ApproverName || '').trim();
    const ticketApproverText = String(row['Task Approver Display'] || row['Task Approver'] || row.Approver || '').trim();
    const empId = String(row['Employee ID'] || row.employeeId || row.EmpID || '').trim().toUpperCase();

    // 1. Direct match from row's approver fields
    const matched = adminApprovers.filter(a => {
      const aId = String(a.id || '').toLowerCase();
      const aName = String(a.name || '').toLowerCase();
      if (!aId && !aName) return false;

      if (approverId && (aId === approverId.toLowerCase() || approverId.toLowerCase().includes(aId))) return true;
      if (approverName && (aName === approverName.toLowerCase() || approverName.toLowerCase().includes(aName))) return true;
      if (ticketApproverText && (
        ticketApproverText.toLowerCase().includes(aName) ||
        ticketApproverText.toLowerCase().includes(aId)
      )) return true;

      return false;
    });

    if (matched.length > 0) return matched;

    // 2. Fallback by employee profile / ID
    if (empId) {
      if (empId === 'KRIS_112') {
        const akash = adminApprovers.find(a => /akash/i.test(a.name) || a.id === 'KRIS_007');
        if (akash) return [akash];
      }
      const empUser = (userOptions || []).find(u => String(u.id || '').toUpperCase() === empId);
      const targetIds = String(empUser?.taskApprover || empUser?.managerId || '')
        .split(',')
        .map(s => s.trim().toLowerCase())
        .filter(Boolean);
      const empMatched = adminApprovers.filter(a => targetIds.includes(String(a.id || '').toLowerCase()));
      if (empMatched.length > 0) return empMatched;
    }

    return adminApprovers;
  }, [isTicketAction, action?.row, adminApprovers, userOptions]);

  // Determine initial admin for on-behalf-of delegation
  const defaultOnBehalfOf = useMemo(() => {
    if (!isPC || !isTicketAction) return '';
    return ticketAssignedApprovers[0]?.id || '';
  }, [isPC, isTicketAction, ticketAssignedApprovers]);

  const [onBehalfOf, setOnBehalfOf] = useState(defaultOnBehalfOf);

  useEffect(() => {
    if (defaultOnBehalfOf) {
      setOnBehalfOf(defaultOnBehalfOf);
    }
  }, [defaultOnBehalfOf]);

  const [remarks, setRemarks] = useState(
    isTicketAction && isPC
      ? isRework
        ? 'Rework requested by PC on behalf of admin.'
        : 'Approved by PC on behalf of admin.'
      : isRework
        ? 'Please rework and resubmit.'
        : 'Approved from Work Track System.'
  );
  const [target, setTarget] = useState(approvers[0]?.id || '');
  const [newPunchIn, setNewPunchIn] = useState(action?.row?.PunchIn && action.row.PunchIn !== '-' ? String(action.row.PunchIn).slice(0, 5) : '');
  const [newPunchOut, setNewPunchOut] = useState(action?.row?.PunchOut && action.row.PunchOut !== '-' ? String(action.row.PunchOut).slice(0, 5) : '');

  function handleSubmit(event) {
    event.preventDefault();
    if (isTransfer && !target) return;
    if (isPC && isTicketAction && !onBehalfOf) {
      alert('Please select the Admin or Super Admin on whose behalf you are acting.');
      return;
    }
    onSubmit(
      isTransfer
        ? { target, remarks }
        : isAttendanceApprove
          ? { remarks, newPunchIn, newPunchOut }
          : { remarks, onBehalfOf: isPC && isTicketAction ? onBehalfOf : undefined }
    );
  }

  return (
    <AppModal
      title={
        isTransfer
          ? 'Transfer Ticket Approval'
          : isRework
            ? isPC && isTicketAction ? 'Reject / Rework on Behalf of Admin' : 'Add Rework Remarks'
            : isPC && isTicketAction ? 'Approve Ticket on Behalf of Admin' : 'Add Approval Remarks'
      }
      onClose={onClose}
      width="580px"
    >
      <form className="ticket-form-grid" onSubmit={handleSubmit}>
        {isPC && isTicketAction ? (
          <div style={{
            background: isRework ? '#fef2f2' : '#f0fdf4',
            border: `1px solid ${isRework ? '#fecaca' : '#bbf7d0'}`,
            color: isRework ? '#991b1b' : '#166534',
            padding: '12px 14px',
            borderRadius: '8px',
            fontSize: '13px',
            gridColumn: '1 / -1',
            display: 'flex',
            flexDirection: 'column',
            gap: '8px'
          }}>
            <div style={{ fontWeight: 600, display: 'flex', alignItems: 'center', gap: '6px' }}>
              <span>🛡️ Process Coordinator (PC) Authority</span>
            </div>
            <span>
              You are acting on behalf of an Admin or Super Admin. Please confirm the authority under which this ticket is being {isRework ? 'sent for rework' : 'approved'}:
            </span>
            <label className="dashboard-control" style={{ margin: 0 }}>
              <span style={{ fontWeight: 600, color: isRework ? '#7f1d1d' : '#14532d' }}>Approving on behalf of:</span>
              <select
                value={onBehalfOf}
                onChange={(event) => setOnBehalfOf(event.target.value)}
                required
                style={{
                  background: '#fff',
                  border: `1px solid ${isRework ? '#f87171' : '#86efac'}`,
                  padding: '8px 10px',
                  borderRadius: '6px',
                  fontWeight: 600,
                  color: '#0f172a'
                }}
              >
                {ticketAssignedApprovers.length > 1 ? (
                  <option value="">-- Select Assigned Admin --</option>
                ) : null}
                {ticketAssignedApprovers.map((admin) => {
                  const roleLabel = (admin.id === 'KRIS_001' || /nandlal/i.test(admin.name)) ? 'Director / Super Admin' : (admin.role || 'Admin');
                  return (
                    <option key={admin.id} value={admin.id}>
                      {admin.name} ({roleLabel})
                    </option>
                  );
                })}
              </select>
            </label>
          </div>
        ) : null}

        {isTransfer ? (
          <label className="dashboard-control">
            <span>Transfer To</span>
            <select value={target} onChange={(event) => setTarget(event.target.value)} required>
              <option value="">Select approver</option>
              {approvers.map((approver) => (
                <option key={approver.id} value={approver.id}>
                  {approver.name} ({approver.id})
                </option>
              ))}
            </select>
          </label>
        ) : null}

        {isAttendanceApprove ? (
          <>
            <div style={{ background: '#eff6ff', color: '#1e3a8a', padding: '12px 16px', borderRadius: '8px', fontSize: '14px', marginBottom: '16px', gridColumn: '1 / -1' }}>
              <span>💡 You can correct punch timings before approving this attendance entry.</span>
            </div>
            <label className="dashboard-control">
              <span>Punch In</span>
              <input type="time" value={newPunchIn} onChange={(event) => setNewPunchIn(event.target.value)} />
            </label>
            <label className="dashboard-control">
              <span>Punch Out</span>
              <input type="time" value={newPunchOut} onChange={(event) => setNewPunchOut(event.target.value)} />
            </label>
          </>
        ) : null}

        <label className="dashboard-control forms-editor__full">
          <span>Remarks</span>
          <textarea rows="4" value={remarks} onChange={(event) => setRemarks(event.target.value)} required />
        </label>

        <div className="ticket-form-actions forms-editor__full">
          <button type="button" className="attendance-cta attendance-cta--gray" onClick={onClose}>Cancel</button>
          <button
            type="submit"
            className={`attendance-cta ${isRework ? 'attendance-cta--red' : 'attendance-cta--blue'}`}
            disabled={saving}
          >
            {saving
              ? 'Submitting...'
              : isTransfer
                ? 'Transfer Approval'
                : isPC && isTicketAction
                  ? isRework ? 'Confirm Rework on Behalf' : 'Confirm Approval on Behalf'
                  : 'Submit Action'}
          </button>
        </div>
      </form>
    </AppModal>
  );
}

